import { createContext, useContext, useEffect, useState } from 'hono/jsx';
import { api } from './api';

export interface User {
  id: string;
  email: string;
  name?: string;
  picture?: string;
  isAdmin?: boolean;
  /** Opt-out for build-complete emails; defaults to on. */
  notifyBuildComplete?: boolean;
  /** Affiliate status; populated by a follow-up probe to `/affiliates/me`. */
  isAffiliate?: boolean;
}

interface AuthValue {
  user: User | null;
  loading: boolean;
  logout: () => Promise<void>;
  refresh: () => Promise<void>;
}

const Ctx = createContext<AuthValue>({
  user: null,
  loading: true,
  logout: async () => {},
  refresh: async () => {},
});

export function AuthProvider({ children }: { children: any }) {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState<boolean>(true);

  async function loadUser() {
    try {
      const r = await api('/me');
      if (!r.ok) { setUser(null); return; }
      const u: User = await r.json();
      setUser(u);
      // Best-effort affiliate probe. 404 is normal (not an affiliate); we just
      // flip the flag if we get a 200 back so the nav can show the Affiliate link.
      api('/affiliates/me').then(async (ar) => {
        if (ar.ok) setUser((prev) => prev ? { ...prev, isAffiliate: true } : prev);
      }).catch(() => {});
    } catch {
      setUser(null);
    }
  }

  useEffect(() => { (async () => { await loadUser(); setLoading(false); })(); }, []);

  async function logout() {
    try { await api('/auth/google/logout', { method: 'POST' }); } catch {}
    setUser(null);
  }

  async function refresh() { await loadUser(); }

  return <Ctx.Provider value={{ user, loading, logout, refresh }}>{children}</Ctx.Provider>;
}

export function useAuth(): AuthValue { return useContext(Ctx); }
